import {
	useEnvironmentDetails,
	useProjectDetails,
	useSites,
} from '../../../../@xmcloud/hooks';
import { useGetEditingSecret } from '../../../services/environments';
import { useGetRepositoryIntegration } from '../../../services/repositories';
import {
	AlertDialogConfirmationModal,
	CircularLoading,
	CodeBlock,
	HeadingMd,
	HeadingSm,
	SelectFormControl,
	TransifexTextAsBold,
	Text,
} from '../../../shared-components';
import { useEffect, useMemo, useState } from 'react';
import { useGetTenant } from '../../../services/platformInventory';
import { Card, CardBody, CardHeader, Divider, Stack } from '@chakra-ui/react';
import { app, devSettings } from '../../../../@xmcloud/core/messages/en';
import { EnumInNumberSourceControl } from '../../../../@xmcloud/types';
import { Toggle } from '../components/developer-settings/ToggleDeveloperSettings';
import { NewJssVersionSettings } from '../components/developer-settings/NewJssVersionSettings';
import { isPhoneDevice } from '../../../../@xmcloud/utils/helpers';
import { CombinedLowerJssSettings } from '../components/developer-settings/CombinedLowerJssSettings';
import { ContentSdkSettings } from '../components/developer-settings/ContentSdkSettings';
import { useGetEnvType } from '../hooks/useGetEnvType';

const {
	chooseSite,
	preview,
	live,
	ContextId,
	info1,
	info2,
	title,
	info,
	txt1,
	txt2,
	txt4,
	noSite,
	step1,
	step2,
	step3,
	title1,
	jssVersionTooltip,
	contextIdTooltip,
} = devSettings;
const { close: closetxt } = app;

const SITE_NAME = 'siteName';
const runCodeLines = [`npm run start:connected`];

const { ADO, GitHub } = EnumInNumberSourceControl;

export const DeveloperSettings: React.FC = () => {
	const { projectId, environmentId, isEhEnv } = useGetEnvType();
	const [selectedFramework, setSelectedFramework] = useState<
		'jss-new' | 'jss-old' | 'content-sdk-1' | 'content-sdk-2'
	>('content-sdk-2');
	const [isPreview, setIsPreview] = useState(true);
	const [siteName, setSiteName] = useState('');
	const [openInfo, setOpenInfo] = useState({ isOpen: false, info: 1 });
	const [isEditingSecretAvailable, setIsEditingSecretAvailable] =
		useState(true);

	const {
		environment,
		cmEnvironmentId,
		platformTenantId,
		isLoading: isEnvironmentLoading,
		repository: environmentRepository,
	} = useEnvironmentDetails(environmentId!);

	const {
		repository: projectRepository,
		sourceControlIntegrationId,
		sourceControlIntegrationName,
		isLoading: projectIsLoading,
	} = useProjectDetails(projectId!);
	const repository = isEhEnv ? environmentRepository : projectRepository;

	// NOTE: Eh environment cannot have it's own contextid because an eh environment is relying on the Authoring environment's contextid
	const currentEnvironmentId = isEhEnv ? cmEnvironmentId : environmentId;

	const { data: editingSecretData, isLoading: editingSecretIsLoading } =
		useGetEditingSecret({
			environmentId: currentEnvironmentId!,
			onNotFoundError: (err) => {
				setIsEditingSecretAvailable(false);
			},
		});

	const { sites, environment: cmEnvironmentDetail } =
		useSites(currentEnvironmentId);

	const { data: srcIntegration, isLoading: isIntegrationDataLoading } =
		useGetRepositoryIntegration(sourceControlIntegrationId);
	const { data: tenantData, isLoading: isGetTenantLoading } = useGetTenant({
		tenantId: platformTenantId,
	});

	const cdpEmbeddedTenantID =
		tenantData?.data?.annotations['XMCloud.CDPEmbeddedTenantID'] || '';
	const editingSecret = editingSecretData?.data || '';
	const provider = srcIntegration?.data?.provider || '';
	const isAdoProvider = provider === ADO;
	const isGitProvider = provider === GitHub;
	const adoRepo = repository?.split('/') || [];

	const gitClone = `git clone https://github.com/${sourceControlIntegrationName}/${repository}.git`;
	const adoClone = `git clone https://dev.azure.com/${adoRepo[0]}/${adoRepo[1]}/_git/${adoRepo[2]}`;

	const checkedoutCodeLine = [
		`${
			isAdoProvider
				? adoClone
				: isGitProvider
					? gitClone
					: 'clone your repository'
		}`,
		...(!!repository
			? [`cd {{repofolder}}\\{{editing hosts folder}}\\{{editing host}}`]
			: ['cd path to your app directory']),
		`npm install`,
	];

	const siteList = useMemo(
		() => sites.map((s) => ({ label: s.name, value: s.name })),
		[sites],
	);

	const isContentSdkSelected =
		selectedFramework === 'content-sdk-1' ||
		selectedFramework === 'content-sdk-2';

	const sdkVersionIndex = useMemo(() => {
		switch (selectedFramework) {
			case 'content-sdk-2':
				return 0;
			case 'content-sdk-1':
				return 1;
			case 'jss-new':
				return 2;
			default:
				return 3;
		}
	}, [selectedFramework]);

	useEffect(() => {
		if (sites.length && !siteName) {
			setSiteName(sites[0].name);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sites.length]);

	const isLoading =
		projectIsLoading ||
		isEnvironmentLoading ||
		editingSecretIsLoading ||
		isIntegrationDataLoading ||
		isGetTenantLoading;

	if (isLoading) return <CircularLoading />;

	return (
		<>
			<Stack
				w="100%"
				py={6}
				direction={['column', 'column', 'row', 'row']}
				gap={8}
			>
				<Stack w={['100%', '100%', '70%', '70%']}>
					<HeadingSm text={title} />
					<Stack
						direction="row"
						gap={6}
						pb="4"
						flexWrap="wrap"
						bgColor={['chakra-subtle-bg', 'chakra-body-bg']}
					>
						<SelectFormControl
							{...{
								options: siteList,
								onChange: (e: any) => {
									setSiteName(e.value);
								},
								label: chooseSite,
								name: SITE_NAME,
								currentValue: siteName,
								maxW: '2xs',
								isDisabled: !siteList.length,
								pt: 0,
								showInfoIcon: true,
								infoText: info1,
								placeholder: siteList.length
									? 'Select...'
									: noSite,
								onInfoIconClick: () =>
									isPhoneDevice &&
									setOpenInfo({
										isOpen: true,
										info: 1,
									}),
							}}
						/>
						<Toggle
							setOpenInfo={setOpenInfo}
							value={sdkVersionIndex}
							onChange={(i) => {
								if (i === 0) {
									setSelectedFramework('content-sdk-2');
								} else if (i === 1) {
									setSelectedFramework('content-sdk-1');
								} else if (i === 2) {
									setSelectedFramework('jss-new');
								} else {
									setSelectedFramework('jss-old');
								}
							}}
							dataTestId1="content-sdk-2-toggle"
							dataTestId2="content-sdk-toggle"
							dataTestId3="new-jss-version-toggle"
							dataTestId4="old-jss-version-toggle"
							tabText1={'Content SDK 2.x'}
							tabText2={'Content SDK 1.x'}
							tabText3={'JSS 21.6+'}
							tabText4={'JSS 21.5 or earlier'}
							title={'SDK version'}
							tooltipText={jssVersionTooltip}
						/>
						<Toggle
							setOpenInfo={setOpenInfo}
							value={isPreview ? 0 : 1}
							onChange={(i) => setIsPreview(i === 0)}
							dataTestId1="contextid-preview-button"
							dataTestId2="contextid-live-button"
							tabText1={preview}
							tabText2={live}
							title={ContextId}
							tooltipText={
								selectedFramework === 'jss-old'
									? contextIdTooltip
									: info2
							}
							isDisabled={selectedFramework === 'jss-old'}
						/>
					</Stack>
					{selectedFramework === 'jss-new' && (
						<NewJssVersionSettings
							{...{
								environmentDetail: isEhEnv
									? cmEnvironmentDetail
									: environment,
								isPreview,
								editingSecret,
								siteName,
								isEditingSecretAvailable,
							}}
						/>
					)}
					{isContentSdkSelected && (
						<ContentSdkSettings
							environmentDetail={
								isEhEnv ? cmEnvironmentDetail : environment
							}
							isPreview={isPreview}
							siteName={siteName}
							editingSecret={editingSecret}
							isEditingSecretAvailable={isEditingSecretAvailable}
							sdkVariant={
								selectedFramework === 'content-sdk-2'
									? '2.0+'
									: '1.0'
							}
						/>
					)}
					{selectedFramework === 'jss-old' && (
						<CombinedLowerJssSettings
							{...{
								siteName,
								cdpEmbeddedTenantID,
								sites,
								currentEnvironmentId: currentEnvironmentId!,
							}}
						/>
					)}
					<Stack>
						<Divider py="3" />
						<Stack pt={[10, 10, 5, 5]}>
							<HeadingSm text={title1} />
							<Stack gap={5}>
								<Stack>
									<Text pb={[4, 4, 1, 1]} text={step1} />
									<CodeBlock
										items={checkedoutCodeLine}
										clipboardText={checkedoutCodeLine.join(
											'\n',
										)}
										data-testid="code-block"
										key="checkout-code-block"
									/>
								</Stack>
								<Text w="full" text={step2} />
								<Stack>
									<Text w="full" text={step3} />
									<CodeBlock
										items={
											isContentSdkSelected
												? ['npm run dev']
												: runCodeLines
										}
										clipboardText={(isContentSdkSelected
											? ['npm run dev']
											: runCodeLines
										).join('\n')}
										data-testid="code-block"
										key="run-code-block"
									/>
								</Stack>
							</Stack>
						</Stack>
					</Stack>
				</Stack>
				<Stack w={['100%', '100%', '30%', '30%']} pt={[2, 2, 8, 8]}>
					<Card variant="filled">
						<CardHeader>
							<HeadingMd text={info} />
						</CardHeader>
						<CardBody>
							<Text text={txt1} />
							<Text py={3} text={txt2} />
							<TransifexTextAsBold
								text={txt4}
								boldText={
									selectedFramework !== 'jss-old'
										? 'Site, SDK version'
										: 'Site '
								}
								boldText2={
									selectedFramework !== 'jss-old'
										? 'Context'
										: 'SDK version'
								}
							/>
						</CardBody>
					</Card>
				</Stack>
			</Stack>
			{openInfo.isOpen && (
				<AlertDialogConfirmationModal
					{...{
						isOpen: openInfo.isOpen,
						onClose: () => setOpenInfo({ isOpen: false, info: 1 }),
						shouldShowActionBtn: false,
						rightButtonText: closetxt,
					}}
				>
					<Text mt="4" text={openInfo.info === 1 ? info1 : info2} />
				</AlertDialogConfirmationModal>
			)}
		</>
	);
};
